import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Container, Group, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Model, surveyLocalization, SurveyModel } from 'survey-core';
import { Survey as SurveyComponent } from 'survey-react-ui';
import { PlainLightPanelless } from 'survey-core/themes';
import 'survey-core/i18n/french';
import { observer } from 'mobx-react';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { useGetActiveEICFConsent } from '../../../global/entities/EICFConsents/queries/useGetActiveEICFConsent.tsx';
import { useSubmitEICFPatientSignature } from '../../../global/entities/EICFConsents/mutations/useSubmitEICFPatientSignature.tsx';
import {
    EICFConsentStatus,
    EICFConsentTemplateKind,
} from '../../../global/entities/EICFConsents/EICFConsent.ts';
import { AppMedError } from '../../../global/error/AppMedError.ts';
import EICFFillablePdfSigner from './EICFFillablePdfSigner.tsx';

surveyLocalization.supportedLocales = ['en', 'fr'];

const EICFConsentPage: React.FC = observer(() => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { data: consent, isLoading, refetch } = useGetActiveEICFConsent(true);
    const submitSignature = useSubmitEICFPatientSignature();
    const [error, setError] = useState<string | null>(null);

    const survey: SurveyModel | null = useMemo(() => {
        if (!consent?.templateRawJson) return null;
        try {
            const model = new Model(consent.templateRawJson);
            model.applyTheme(PlainLightPanelless);
            model.locale = i18n.language;
            return model;
        } catch (e) {
            console.error('Failed to load consent survey JSON', e);
            return null;
        }
    }, [consent?.templateRawJson, i18n.language]);

    useEffect(() => {
        if (!survey || !consent) return;
        const handleComplete = async (sender: SurveyModel) => {
            setError(null);
            try {
                await submitSignature.mutateAsync({
                    consentID: consent.id,
                    answersJson: JSON.stringify(sender.data),
                });
                await refetch();
            } catch (e) {
                if (e instanceof AppMedError) setError(e.message);
                else setError(t('eicf.page.unexpectedError'));
            }
        };
        survey.onComplete.add(handleComplete);
        return () => {
            survey.onComplete.remove(handleComplete);
        };
    }, [survey, consent, submitSignature, refetch, t]);

    if (isLoading) {
        return (
            <Container py="xl">
                <Loader />
            </Container>
        );
    }

    if (!consent) {
        return (
            <Container py="xl">
                <Alert icon={<IconCheck size="1rem" />} color="green" title={t('eicf.page.noConsentTitle')}>
                    {t('eicf.page.noConsentBody')}
                </Alert>
                <Group mt="md">
                    <Button onClick={() => navigate('/patientDashboard/mainPatient')}>
                        {t('eicf.page.backToDashboard')}
                    </Button>
                </Group>
            </Container>
        );
    }

    if (consent.status !== EICFConsentStatus.PendingPatientSignature) {
        // Patient has signed; show pending physician message.
        return (
            <Container py="xl">
                <Alert icon={<IconCheck size="1rem" />} color="green" title={t('eicf.page.signedTitle')}>
                    {t('eicf.page.signedAwaitingPhysician')}
                </Alert>
                <Group mt="md">
                    <Button onClick={() => navigate('/patientDashboard/mainPatient')}>
                        {t('eicf.page.backToDashboard')}
                    </Button>
                </Group>
            </Container>
        );
    }

    return (
        <Container py="lg">
            <Stack gap="md">
                <Title order={2}>
                    {i18n.language === 'fr' ? consent.templateNameFr : consent.templateNameEn}
                </Title>
                <Text size="sm" c="dimmed">
                    {t('eicf.page.versionLabel', { version: consent.templateVersion })}
                </Text>
                {error && (
                    <Alert icon={<IconAlertCircle size="1rem" />} color="red" title={t('eicf.page.errorTitle')}>
                        {error}
                    </Alert>
                )}
                <Paper p="md" withBorder>
                    {consent.templateKind ===
                    EICFConsentTemplateKind.FillablePdf ? (
                        <EICFFillablePdfSigner
                            consent={consent}
                            onSigned={refetch}
                        />
                    ) : survey ? (
                        <SurveyComponent model={survey} />
                    ) : (
                        <Text c="red">{t('eicf.page.surveyLoadError')}</Text>
                    )}
                </Paper>
            </Stack>
        </Container>
    );
});

export default EICFConsentPage;
